import React, { useEffect, useState, useTransition } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { userModule } from "../../api/modules/user.module";
import addressModule from "../../api/modules/address.module";
import buyerModule from "../../api/modules/buyer.module";
import sellarModule from "../../api/modules/seller.module";
import { showToast } from "../../utils/toast";
import { useAuth } from "../../hooks/useAuth";
import {
  useBankDetails,
  useAddBankDetails,
  useUpdateBankDetails,
  useDeleteBankDetails,
} from "../../hooks/useBankDetails";
import { updateUser } from "../../store/slices/authSlice";
import { getUser as getStoredUser } from "../../auth/tokenStorage";

import ProfileView from "./Profile.view";

/**
 * /profile, the container. Queries, mutations, the role gate, toasts, and the
 * one Redux write-back.
 *
 * Everything visual lives in `Profile.view.jsx`, which is pure, so the state
 * explorer can mount the real screen without any of this running. See
 * `design-system/explorer/registry.js` for the convention.
 *
 * WHAT MOVED UP INTO THIS FILE, AND WHY
 * -------------------------------------
 * Five of the eight sections used to fetch their own data: `SavedAddresses`,
 * `DeliverySection`, `RecentOrdersSection`, `SavedCardsSection` and the store
 * availability mutation inside `StoreDetailsSection`. Two of them ran the SAME
 * `userAddresses` query, and the sections could not be rendered at all without
 * a live API, which is exactly what stopped the empty and error states from
 * ever being reviewed. Every one of those queries is now here and every section
 * is pure.
 *
 * The `Layout type="header"` wrapper went with them. That component ships dead
 * Tailwind class names (`fixed top-0 ...`) for a Tailwind that is not installed,
 * so it was never positioning anything; `MarketShell` is the real chrome.
 */

const Profile = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { getUserRole, logout } = useAuth();
  const [, startTransition] = useTransition();

  const isBuyer = getUserRole()?.toLowerCase() === "buyer";

  /* ------------------------------------------------------------- dialogs */

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editDeliveryOpen, setEditDeliveryOpen] = useState(false);
  const [editStoreOpen, setEditStoreOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);

  const [bankFormOpen, setBankFormOpen] = useState(false);
  const [editingBankDetails, setEditingBankDetails] = useState(null);
  const [deleteBankOpen, setDeleteBankOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteAddressOpen, setDeleteAddressOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [storeUnavailable, setStoreUnavailable] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  /* -------------------------------------------------------------- queries */

  const bankDetailsQuery = useBankDetails(isBuyer ? { enabled: false } : {});
  const addBankDetailsMutation = useAddBankDetails();
  const updateBankDetailsMutation = useUpdateBankDetails();
  const deleteBankDetailsMutation = useDeleteBankDetails();

  const profileQuery = useQuery({
    queryKey: ["sellerProfile"],
    queryFn: async () => {
      const res = await userModule.getSellerProfile();
      if (!res.success) throw new Error(res.message || "Failed to load profile");
      return res.data?.details || res.data?.data || res.data;
    },
  });

  const walletQuery = useQuery({
    queryKey: ["wallet"],
    queryFn: async () => {
      const res = await userModule.getWallet();
      if (!res.success) throw new Error(res.message || "Failed to load wallet");
      return res.data?.details || res.data?.data || res.data;
    },
    enabled: !isBuyer,
  });

  const sellerInfoQuery = useQuery({
    queryKey: ["sellerInfo"],
    queryFn: async () => {
      const res = await userModule.getSellerInfo();
      if (!res.success) throw new Error(res.message || "Failed to load seller info");
      return res.data?.details || res.data?.data || res.data;
    },
    enabled: !isBuyer,
  });

  const deliveryQuery = useQuery({
    queryKey: ["deliveryInfo"],
    queryFn: async () => {
      const res = await userModule.getDeliveryInfo();
      if (!res.success) throw new Error(res.message || "Failed to load delivery");
      const info = res.data?.details || res.data?.data || res.data;
      return Array.isArray(info?.addresses) ? info.addresses[0] : info?.address || info;
    },
    enabled: !isBuyer,
  });

  // One query, one cache key. `SavedAddresses` and `DeliverySection` each ran
  // their own copy of this before, so a phone hitting /profile fetched the same
  // list twice.
  const addressesQuery = useQuery({
    queryKey: ["userAddresses"],
    queryFn: async () => {
      const res = await addressModule.getAddresses();
      if (!res.success) throw new Error(res.message);
      return res.data || [];
    },
  });

  const ordersQuery = useQuery({
    queryKey: ["recent-orders"],
    queryFn: async () => {
      const { success, data } = await buyerModule.getBuyerOrders({ page: 1, limit: 4 });
      if (!success) return { orders: [] };
      return data || { orders: [] };
    },
    enabled: isBuyer,
  });

  const savedCardsQuery = useQuery({
    queryKey: ["saved-cards-preview"],
    queryFn: async () => {
      const { success, data } = await buyerModule.getSavedCards({ page: 1, limit: 4 });
      if (!success) return { favourites: [] };
      return data || { favourites: [] };
    },
    enabled: isBuyer,
  });

  useEffect(() => {
    if (sellerInfoQuery.data?.is_store_unavailable !== undefined) {
      setStoreUnavailable(sellerInfoQuery.data.is_store_unavailable);
    }
  }, [sellerInfoQuery.data?.is_store_unavailable]);

  /* ------------------------------------------------------------ mutations */

  const updateProfileMutation = useMutation({
    mutationFn: (formData) => userModule.updateSellerProfile(formData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sellerProfile"] });
      await queryClient.invalidateQueries({ queryKey: ["deliveryInfo"] });
      await queryClient.invalidateQueries({ queryKey: ["sellerInfo"] });
    },
  });

  const updateBuyerProfileMutation = useMutation({
    mutationFn: (formData) => userModule.updateBuyerProfile(formData),
    onSuccess: async () => {
      const updatedProfile = await queryClient.fetchQuery({
        queryKey: ["sellerProfile"],
        queryFn: () =>
          userModule.getSellerProfile().then((res) => {
            if (!res.success) throw new Error(res.message || "Failed to load profile");
            return res.data?.details || res.data?.data || res.data;
          }),
      });

      if (updatedProfile?.full_name) {
        const existingUser = getStoredUser() || {};
        dispatch(updateUser({ ...existingUser, full_name: updatedProfile.full_name }));
      }
    },
  });

  const updateDeliveryMutation = useMutation({
    mutationFn: (payload) => userModule.updateDeliveryInfo(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["deliveryInfo"] });
      await queryClient.invalidateQueries({ queryKey: ["sellerProfile"] });
      // The store address is also a row in Saved addresses.
      await queryClient.invalidateQueries({ queryKey: ["userAddresses"] });
    },
  });

  const updateStoreInfoMutation = useMutation({
    mutationFn: (formData) => userModule.updateSellerInfo(formData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sellerInfo"] });
      await queryClient.invalidateQueries({ queryKey: ["sellerProfile"] });
    },
  });

  const addAddressMutation = useMutation({
    mutationFn: (addressData) => addressModule.addAddress(addressData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userAddresses"] });
      showToast.success("Address added successfully");
      setAddressFormOpen(false);
      setEditingAddress(null);
    },
    onError: (error) => showToast.error(error.message || "Failed to add address"),
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ id, data }) => addressModule.updateAddress(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userAddresses"] });
      showToast.success("Address updated successfully");
      setAddressFormOpen(false);
      setEditingAddress(null);
    },
    onError: (error) => showToast.error(error.message || "Failed to update address"),
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (id) => addressModule.deleteAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userAddresses"] });
      showToast.success("Address deleted successfully");
      setDeleteAddressOpen(false);
      setAddressToDelete(null);
    },
    onError: (error) => showToast.error(error.message || "Failed to delete address"),
  });

  /* -------------------------------------------------------------- handlers */

  const handleToggleAvailability = async (unavailable) => {
    const previous = storeUnavailable;
    setAvailabilityLoading(true);
    setStoreUnavailable(unavailable);
    try {
      const res = await sellarModule.updateStoreAvailability(unavailable);
      if (res.success) {
        showToast.success(
          unavailable ? "Store marked as unavailable" : "Store marked as available",
        );
        await queryClient.invalidateQueries({ queryKey: ["sellerProfile"] });
        await queryClient.invalidateQueries({ queryKey: ["sellerInfo"] });
      } else {
        setStoreUnavailable(previous);
        showToast.error(res.error || "Failed to update store availability");
      }
    } catch (err) {
      setStoreUnavailable(previous);
      showToast.error("Something went wrong");
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleSubmitProfile = async (formData) => {
    const mutation = isBuyer ? updateBuyerProfileMutation : updateProfileMutation;
    startTransition(async () => {
      setSaving(true);
      const res = await mutation.mutateAsync(formData);
      if (!res?.success) {
        showToast.error(res?.message || "Failed to update profile");
      } else {
        showToast.success("Profile updated successfully");
      }
      setEditProfileOpen(false);
      setSaving(false);
    });
  };

  const handleSubmitDelivery = async (payload) => {
    startTransition(async () => {
      setSaving(true);
      const res = await updateDeliveryMutation.mutateAsync(payload);
      if (!res?.success) {
        showToast.error(res?.message || "Failed to update delivery info");
      } else {
        showToast.success("Delivery address updated");
      }
      setEditDeliveryOpen(false);
      setSaving(false);
    });
  };

  const handleSubmitStore = async (formData) => {
    startTransition(async () => {
      setSaving(true);
      const res = await updateStoreInfoMutation.mutateAsync(formData);
      if (!res?.success) {
        showToast.error(res?.message || "Failed to update store info");
      } else {
        showToast.success("Store info updated successfully");
      }
      setEditStoreOpen(false);
      setSaving(false);
    });
  };

  const handleSubmitBank = async (formData) => {
    setSaving(true);
    try {
      const result = editingBankDetails
        ? await updateBankDetailsMutation.mutateAsync(formData)
        : await addBankDetailsMutation.mutateAsync(formData);

      showToast.success(
        editingBankDetails
          ? "Payment method updated successfully"
          : "Payment method added successfully",
      );

      await queryClient.invalidateQueries({ queryKey: ["bankDetails"] });

      if (result) {
        setBankFormOpen(false);
        setEditingBankDetails(null);
      }
    } catch (error) {
      showToast.error(error.message || "Failed to save payment method");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteBank = async () => {
    if (!accountToDelete) return;

    setDeleting(accountToDelete);
    try {
      const result = await deleteBankDetailsMutation.mutateAsync(accountToDelete);
      await queryClient.invalidateQueries({ queryKey: ["bankDetails"] });
      if (result) showToast.success("Payment method deleted successfully");
    } catch (error) {
      showToast.error(error.message || "Failed to delete payment method");
    } finally {
      setDeleting(null);
      setDeleteBankOpen(false);
      setAccountToDelete(null);
    }
  };

  const handleSubmitAddress = (formData) => {
    if (editingAddress) {
      updateAddressMutation.mutate({ id: editingAddress.id, data: formData });
    } else {
      addAddressMutation.mutate(formData);
    }
  };

  /* ----------------------------------------------------------------- gate */

  const loading =
    !isBuyer &&
    (profileQuery.isLoading || walletQuery.isLoading || deliveryQuery.isLoading);

  const error = !isBuyer
    ? profileQuery.error?.message ||
      walletQuery.error?.message ||
      deliveryQuery.error?.message
    : profileQuery.error?.message;

  return (
    <ProfileView
      isBuyer={isBuyer}
      /* Sign out is in the mobile account menu, because below the handoff
         there is no `UserMenu` to hold it. Same `logout` the desktop menu
         calls, so there is one implementation and not two. */
      onSignOut={logout}
      loading={loading}
      error={error}

      profile={profileQuery.data}
      wallet={walletQuery.data}
      sellerInfo={sellerInfoQuery.data}
      sellerInfoLoading={sellerInfoQuery.isLoading}
      sellerInfoError={sellerInfoQuery.error}
      deliveryAddress={deliveryQuery.data}

      addresses={addressesQuery.data || []}
      addressesLoading={addressesQuery.isLoading}

      bankDetails={bankDetailsQuery.data}
      bankDetailsLoading={bankDetailsQuery.isLoading}
      bankDetailsError={bankDetailsQuery.error}

      orders={ordersQuery.data?.orders || []}
      ordersLoading={ordersQuery.isLoading}
      savedCards={savedCardsQuery.data?.favourites || []}
      savedCardsLoading={savedCardsQuery.isLoading}

      storeUnavailable={storeUnavailable}
      availabilityLoading={availabilityLoading}
      onToggleAvailability={handleToggleAvailability}

      saving={saving}
      deleting={deleting}

      editProfileOpen={editProfileOpen}
      onOpenEditProfile={() => setEditProfileOpen(true)}
      onCloseEditProfile={() => setEditProfileOpen(false)}
      onSubmitProfile={handleSubmitProfile}

      editDeliveryOpen={editDeliveryOpen}
      onOpenEditDelivery={() => setEditDeliveryOpen(true)}
      onCloseEditDelivery={() => setEditDeliveryOpen(false)}
      onSubmitDelivery={handleSubmitDelivery}

      editStoreOpen={editStoreOpen}
      onOpenEditStore={() => setEditStoreOpen(true)}
      onCloseEditStore={() => setEditStoreOpen(false)}
      onSubmitStore={handleSubmitStore}

      bankFormOpen={bankFormOpen}
      editingBankDetails={editingBankDetails}
      onOpenAddBank={() => {
        setEditingBankDetails(null);
        setBankFormOpen(true);
      }}
      onOpenEditBank={(account) => {
        setEditingBankDetails(account);
        setBankFormOpen(true);
      }}
      onCloseBankForm={() => {
        setBankFormOpen(false);
        setEditingBankDetails(null);
      }}
      onSubmitBank={handleSubmitBank}

      deleteBankOpen={deleteBankOpen}
      onRequestDeleteBank={(accountId) => {
        setAccountToDelete(accountId);
        setDeleteBankOpen(true);
      }}
      onConfirmDeleteBank={handleConfirmDeleteBank}
      onCancelDeleteBank={() => {
        setDeleteBankOpen(false);
        setAccountToDelete(null);
      }}

      addressFormOpen={addressFormOpen}
      editingAddress={editingAddress}
      addressSubmitting={addAddressMutation.isPending || updateAddressMutation.isPending}
      onOpenAddAddress={() => {
        setEditingAddress(null);
        setAddressFormOpen(true);
      }}
      onOpenEditAddress={(address) => {
        setEditingAddress(address);
        setAddressFormOpen(true);
      }}
      onCloseAddressForm={() => {
        setAddressFormOpen(false);
        setEditingAddress(null);
      }}
      onSubmitAddress={handleSubmitAddress}

      deleteAddressOpen={deleteAddressOpen}
      addressToDelete={addressToDelete}
      addressDeleting={deleteAddressMutation.isPending}
      onRequestDeleteAddress={(address) => {
        setAddressToDelete(address);
        setDeleteAddressOpen(true);
      }}
      onConfirmDeleteAddress={() => {
        if (addressToDelete) deleteAddressMutation.mutate(addressToDelete.id);
      }}
      onCancelDeleteAddress={() => {
        setDeleteAddressOpen(false);
        setAddressToDelete(null);
      }}

      payoutOpen={payoutOpen}
      onOpenPayout={() => setPayoutOpen(true)}
      onClosePayout={() => setPayoutOpen(false)}
      onPayoutSuccess={() => {
        // Deliberately NOT logging the payout payload. It carries the amount and
        // the destination account; the previous version console.logged all of it.
        showToast.success("Payout requested");
        queryClient.invalidateQueries({ queryKey: ["wallet"] });
      }}
    />
  );
};

export default Profile;
